<template>
  <div :class="['ModalCmp', { 'has-inner-scroll': dataHasInnerScroll }]">
    <modal
      :adaptive="true"
      :pivot-y="0.2"
      width="100%"
      height="100%"
      :click-to-close="dataClickToClose"
      :name="dataName"
      @before-open="beforeOpen"
      @before-close="beforeClose"
      @closed="closed"
      @opened="opened"
    >
      <!--
        For invoking the modal you should add `@click="$modal.show('name-of-the-modal')"` on the element that should open the modal

        Example:
        <button @click="$modal.show('name-of-the-modal')">APRI</button>
      -->
      <div
        class="modal-container"
        @click.self="dataClickToClose && $modal.hide(dataName)"
      >
        <div
          ref="$modalBox"
          :class="[
            'modal-box',
            overwriteClasses,
            {
              'has-button': dataHasBackButton || dataHasCloseButton,
            },
          ]"
        >
          <slot />
          <button
            v-if="dataHasBackButton && mdAndDown"
            class="modal-back"
            type="button"
            @click="$modal.hide(dataName)"
          >
            <span class="sr-only">Back</span>
          </button>
          <button
            v-else-if="dataHasCloseButton"
            class="modal-close"
            type="button"
            @click="$modal.hide(dataName)"
          >
            <span class="sr-only">Close</span>
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { mdAndDown } from '@/helpers/breakpoints.js';

export default {
  name: 'ModalCmp',
  props: {
    dataName: {
      type: String,
      default: 'custom-modal',
    },
    dataHasCloseButton: {
      type: Boolean,
      default: true,
    },
    dataHasInnerScroll: {
      type: Boolean,
      default: false,
    },
    dataClickToClose: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    return { mdAndDown };
  },
  data() {
    return {
      dataHasBackButton: false,
      overwriteClasses: [],
    };
  },
  mounted() {
    this.onClassChange(this.$el.classList.value);
    this.observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        const newValue = mutation.target.getAttribute(mutation.attributeName);
        this.$nextTick(() => {
          this.onClassChange(newValue, mutation.oldValue);
        });
      }
    });

    this.observer.observe(this.$el, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class'],
    });
  },
  beforeDestroy() {
    this.observer.disconnect();
  },
  methods: {
    onClassChange(classAttrValue) {
      // console.log('onClassChange');
      const overwriteClassesPrefix = ['bg-color-', 'pt-', 'pb-'];
      overwriteClassesPrefix.forEach((prefix) => {
        const index = this.overwriteClasses.findIndex((e) =>
          e.includes(prefix)
        );
        if (index !== -1) {
          this.overwriteClasses.splice(index, 1);
        }
        if (classAttrValue.includes(prefix)) {
          this.overwriteClasses.push(
            classAttrValue
              .trim()
              .split(' ')
              .find((e) => e.includes(prefix))
          );
        }
      });
    },
    beforeOpen(e) {
      this.dataHasBackButton =
        e.params && e.params.dataHasBackButton
          ? e.params.dataHasBackButton
          : false;
      /**
       * remember to put a watch on the entrypoint
       * watch: {
       *   $currentScrollPosition: {
       *     handler() {
       *       document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
       *     },
       *     immediate: true
       *   }
       * }
       * https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/
       * https://vuilaptrinh.com/2019-06-07-huong-dang-xu-ly-modal-va-scroll/
       * */
      const scrollY =
        document.documentElement.style.getPropertyValue('--scroll-y');
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}`;
    },
    opened() {
      this.$_a11Support_clearAllBodyScrollLocks();
      this.$emit('opened');
      // this.$nextTick(() => {
      //   // modal-box
      //   const element = document.querySelector(
      //     '.vm--modal .modal-box'
      //   ).firstChild;
      //   element.focus();
      //   const wrapper = document.querySelector('.vm--modal .modal-box');
      //   // we do not set focus on the scrolling element fo a strange bug on ios
      //   this.$_a11Support_setVoiceOverFocus(wrapper);
      //   this.$_a11Support_trapScreenReader({ $el: element, _uid: this._uid });
      //   // we do set scrollable the correct element
      //   this.$_a11Support_disableScrollOuterElement({
      //     $el: element,
      //     enable: true,
      //   });
      // });
    },
    closed() {
      // console.log('closed');
      // this.$_a11Support_untrapScreenReader({ _uid: this._uid });
      // this.$_a11Support_clearAllBodyScrollLocks();
      this.$emit('closed');
    },
    beforeClose() {
      // console.log('beforeClose');
      // this.$_a11Support_clearAllBodyScrollLocks();
      const body = document.body;
      const scrollY = body.style.top;
      body.style.position = '';
      body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    },
    trapFocus(el) {
      const focusableElements = this.$el.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      );
      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      this.$el.addEventListener('keydown', (e) => {
        const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
        if (e.key === 'Escape') {
          this.$modal.hide(this.dataName);
          return;
        }
        if (!isTabPressed) {
          return;
        }
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      });

      if (!el) {
        firstFocusableElement.focus();
      } else {
        el.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin on-custom-hover() {
  @media (hover: hover),
    (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    &:hover {
      @content;
    }
  }

  @media (hover: none) {
    &:active {
      @content;
    }
  }
}

@mixin modal-box-sizes {
  width: 100%;
  height: 100%;
  max-height: 100vh;
  max-height: 100svh;
  overflow: hidden;
  overflow-y: auto;

  @include media-breakpoint-up(md) {
    width: 83.333333%; /* col-10 */
    height: auto;
    max-height: 80vh;
    max-height: 80svh;
  }

  @include media-breakpoint-up(lg) {
    width: 66.666667%; /* col-8 */
  }
}

@mixin scrollbar-crossbrowser-style {
  /* Works on Firefox */
  scrollbar-width: thin;
  scrollbar-color: $color-grey-600-50 transparent;

  /* Works on Chrome/Edge/Safari */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 15px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px $color-grey-600-75;
    border-radius: 20px;
    border: 4px solid transparent;
  }
}

.ModalCmp {
  background: none !important;
  padding: 0 !important;

  ::v-deep .vm--container {
    z-index: 99999999999;
  }

  ::v-deep .vm--modal {
    background: transparent;
    box-shadow: none;
    border-radius: 0;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  ::v-deep .vm--overlay {
    background: transparent;

    @include media-breakpoint-up(md) {
      background: var(--modal-overlay, $color-grey-800-75);
    }
  }

  .modal-container {
    height: 100%;

    @include media-breakpoint-up(md) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: calc(
        100% - #{fetch($container-padding-x-resp, md, $container-padding-x)}
      );
      margin: 0 auto;
      background: transparent;
    }

    @include media-breakpoint-up(lg) {
      width: calc(
        100% - #{fetch($container-padding-x-resp, lg, $container-padding-x)}
      );
    }

    @include media-breakpoint-up(xl) {
      width: calc(
        100% - #{fetch($container-padding-x-resp, xl, $container-padding-x)}
      );
      max-width: #{map_get($container-max-widths, xl)};
    }

    .modal-box {
      position: relative;
      margin: 0 auto;
      background: var(--modal-bg-color, $color-white);
      @include scrollbar-crossbrowser-style;
      @include modal-box-sizes;

      @include media-breakpoint-up(md) {
        border-radius: 4px;
        filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.15));
      }
    }
  }

  &.has-inner-scroll {
    .modal-container {
      .modal-box {
        overflow-y: hidden;

        &.has-button {
          padding: 72px 0 0;
        }
      }
    }

    .modal-box > :first-child {
      height: 100%;
      border-top: 1px solid
        var(--modal-content-separator-color, $color-grey-400);
      overflow: hidden;
      overflow-y: auto;
      @include scrollbar-crossbrowser-style;
    }
  }

  /* Buttons */
  --modal-close-color: #{$color-grey-900};
  --modal-close-hover-color: #{$color-grey-800};

  .modal-back,
  .modal-close {
    position: absolute;
    top: 24px;
    left: 24px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    z-index: 999999;

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      border: 0;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }
  }

  .modal-back {
    &::before {
      content: '';
      display: block;
      position: absolute;
      left: 50%;
      top: 50%;
      padding: 3.5px;
      border-style: solid;
      border-width: 0 2px 2px 0;
      border-color: var(--modal-close-color);
      transition: border-color 0.3s ease;
      transform: translate(-33%, -50%) rotate(135deg);
    }

    @include on-custom-hover() {
      &::before {
        border-color: var(--modal-close-hover-color);
      }
    }
  }

  .modal-close {
    left: auto;
    right: 24px;

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      left: 50%;
      top: 50%;
      width: 16px;
      height: 2px;
      background: var(--modal-close-color);
      margin: -1px 0 0 -8px; /* -half-height 0 0 -half-width */
      transition: background-color 0.3s ease;
    }

    &::before {
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }

    @include on-custom-hover() {
      &::before,
      &::after {
        background: var(--modal-close-hover-color);
      }
    }
  }
}
</style>
